import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useLogout } from 'react-admin';
import { usePermissions } from '../context/PermissionContext';
import { pageKeyForPath, ADMIN_SECTIONS, allowsAdmin } from '../config/adminPages';

const labelFor = (pageKey: string): string => {
  for (const s of ADMIN_SECTIONS) for (const p of s.pages) if (p.key === pageKey) return `${s.label} › ${p.label}`;
  return pageKey;
};

/**
 * The route-level half of per-user page access. The sidebar hides pages a
 * user's allow-list does not admit; this refuses them when typed into the
 * address bar or reached by a stale link. Paths outside the registry (the
 * dashboard, redirects) are never gated, and a null list gates nothing.
 */
export default function PageGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { canSeePath, loading, pagePermissions } = usePermissions();
  const logout = useLogout();

  // A tablet-only account (its list holds only the Tablet tick) has no admin
  // at all — not even the dashboard, which no page tick gates.
  if (!loading && !allowsAdmin(pagePermissions)) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }} data-testid="tablet-only">
        <Typography variant="h5" sx={{ mb: 1 }}>This account is for the order tablet only</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Sign in on the store&apos;s tablet instead. Ask an admin to tick admin pages under Staff Users → Page access if you need the dashboard.
        </Typography>
        <Button variant="outlined" onClick={() => logout()}>Sign out</Button>
      </Box>
    );
  }

  if (loading || canSeePath(pathname)) return <>{children}</>;

  const key = pageKeyForPath(pathname) ?? pathname;
  return (
    <Box sx={{ p: 6, textAlign: 'center' }} data-testid="page-gate">
      <Typography variant="h5" sx={{ mb: 1 }}>This page is not part of your access</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {labelFor(key)} is not on the list of pages an admin has given your account. Ask an admin to add it under Staff Users → Page access.
      </Typography>
      <Button variant="outlined" href="/">Back to dashboard</Button>
    </Box>
  );
}
